import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Upload, 
  Trash2, 
  Plus, 
  Save,
  Image as ImageIcon,
  AlertCircle,
  Loader2,
  X,
  Check,
  ChevronDown,
  Info,
  Eye,
  Download,
  IndianRupee,
  Shield,
  Package
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import FirebaseProductService from '../../../../services/product.service';
import { auth, onAuthStateChanged } from '../../../../firebase/config';

const AddProductPage = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    category: '',
    name: '',
    description: '',
    specifications: '',
    quantity: '',
    unit: 'PCS',
    price: '',
    currency: 'INR',
    gst: '',
    moq: '', // NEW: Minimum Order Quantity
    warranty: '', // NEW: Warranty/Guarantee details
    status: 'PENDING',
    colors: ''
  });

  const [images, setImages] = useState([
    {
      id: 1,
      url: null,
      name: 'Attachment 1',
      originalName: 'Attachment 1',
      isPlaceholder: true,
      isUploaded: false,
      file: null,
      previewUrl: null
    },
    {
      id: 2,
      url: null,
      name: 'Attachment 2',
      originalName: 'Attachment 2',
      isPlaceholder: true,
      isUploaded: false,
      file: null,
      previewUrl: null
    }
  ]);

  const [charCountName, setCharCountName] = useState(0);
  const [charCountDesc, setCharCountDesc] = useState(0);
  const [charCountSpec, setCharCountSpec] = useState(0);
  const [charCountWarranty, setCharCountWarranty] = useState(0);

  const categories = [
    'Automobile & Car Accessories',
    'Home & Kitchen',
    'Electronics & Gadgets',
    'Industrial & Machinery',
    'Packaging & Materials',
    'Toys & Gifts',
    'Mobile Accessories',
  ];

  const units = ['PCS', 'KG', 'L', 'M', 'M²', 'M³', 'SET', 'BOX', 'PAIR', 'ROLL', 'TON', 'LOT'];
  const currencies = ['INR', 'USD', 'EUR', 'GBP', 'CNY', 'JPY', 'AUD', 'CAD'];
  const gstRates = ['5%', '10%', '12%', '18%', '28%'];

  const warrantyOptions = [
    'No Warranty',
    '3 Months',
    '6 Months',
    '1 Year',
    '2 Years',
    '3 Years',
    '5 Years',
    'Lifetime',
    'Manufacturer Warranty',
    'Seller Warranty'
  ];

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUser(user);
      } else {
        toast.error('Please login to add products');
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Update character count
    if (name === 'name') {
      setCharCountName(value.length);
    } else if (name === 'description') {
      setCharCountDesc(value.length);
    } else if (name === 'specifications') {
      setCharCountSpec(value.length);
    } else if (name === 'warranty') {
      setCharCountWarranty(value.length);
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberInput = (e) => {
    const { name, value } = e.target;
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setImageErrors(prev => ({
        ...prev,
        [id]: 'Please upload only JPG, JPEG, PNG, or WebP files.'
      }));
      toast.error('Please upload only JPG, JPEG, PNG, or WebP files.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setImageErrors(prev => ({
        ...prev,
        [id]: 'File size must be less than 5MB.'
      }));
      toast.error('File size must be less than 5MB.');
      return;
    }

    // Clear any previous error
    setImageErrors(prev => ({ ...prev, [id]: null }));

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    const newImages = images.map(img => 
      img.id === id 
        ? { 
            ...img, 
            file: file,
            name: file.name, 
            url: previewUrl, 
            isPlaceholder: false,
            isUploaded: false,
            previewUrl: previewUrl
          }
        : img
    );
    setImages(newImages);
    
    toast.success('Image uploaded successfully!');
    e.target.value = null;
  };

  const handleAttachmentBoxClick = (id) => {
    document.getElementById(`file-${id}`).click();
  };

  const removeImage = (id) => {
    const newImages = images.map(img => {
      if (img.id === id) {
        // Revoke object URL to prevent memory leaks
        if (img.previewUrl && !img.isUploaded) {
          URL.revokeObjectURL(img.previewUrl);
        }
        
        return { 
          ...img, 
          file: null, 
          url: null, 
          previewUrl: null,
          name: img.originalName, 
          isPlaceholder: true,
          isUploaded: false
        };
      }
      return img;
    });
    setImages(newImages);
    
    // Clear any error for this image
    setImageErrors(prev => ({ ...prev, [id]: null }));
    
    if (selectedImage?.id === id) {
      setSelectedImage(null);
    }
    toast.success('Image removed');
  };

  const viewImage = (image) => {
    if (image.url || image.previewUrl) {
      setSelectedImage(image);
    }
  };

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  const addImageSlot = () => {
    if (images.length < 6) {
      const newId = images.length + 1;
      setImages([
        ...images,
        {
          id: newId,
          url: null,
          name: `Attachment ${newId}`,
          originalName: `Attachment ${newId}`,
          isPlaceholder: true,
          isUploaded: false,
          file: null,
          previewUrl: null
        }
      ]);
      toast.success('New image slot added');
    } else {
      toast.error('Maximum 6 images allowed');
    }
  };

  const validateForm = () => {
    // Check required fields
    if (!formData.name.trim()) {
      toast.error('Product name is required.');
      return false;
    }
    if (!formData.category) {
      toast.error('Product category is required.');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error('Product description is required.');
      return false;
    }
    if (!formData.specifications.trim()) {
      toast.error('Product specifications are required.');
      return false;
    }
    if (!formData.colors.trim()) {
      toast.error('Product colors are required.');
      return false;
    }
    if (!formData.quantity || formData.quantity <= 0) {
      toast.error('Please enter a valid quantity.');
      return false;
    }
    if (!formData.moq || formData.moq <= 0) { // NEW MOQ VALIDATION
      toast.error('Please enter a valid Minimum Order Quantity (MOQ).');
      return false;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error('Please enter a valid price.');
      return false;
    }
    if (!formData.gst) {
      toast.error('Please select GST Percentage.');
      return false;
    }
    if (!formData.warranty) { // NEW WARRANTY VALIDATION
      toast.error('Please select warranty/guarantee details.');
      return false;
    }

    // Check if at least one image is uploaded
    const hasAtLeastOneImage = images.some(img => img.file instanceof File);
    if (!hasAtLeastOneImage) {
      toast.error('Please upload at least one product image.');
      return false;
    }

    // Check if first image is uploaded (main product image)
    if (!images[0].file || !(images[0].file instanceof File)) {
      toast.error('First image (main product image) is required.');
      return false;
    }

    // Validate MOQ is not greater than available quantity
    if (parseFloat(formData.moq) > parseFloat(formData.quantity)) {
      toast.error('Minimum Order Quantity (MOQ) cannot be greater than available quantity.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSaving(true);
    
    try {
      // Get image files
      const imageFiles = images
        .filter(img => img.file instanceof File)
        .map(img => img.file);
      
      // Prepare product data - Status is always PENDING
      const productData = {
        name: formData.name.trim(),
        category: formData.category,
        description: formData.description.trim(),
        specifications: formData.specifications
          .split('\n')
          .map(spec => spec.trim())
          .filter(spec => spec.length > 0),
        quantity: parseFloat(formData.quantity) || 0,
        unit: formData.unit,
        price: parseFloat(formData.price) || 0,
        currency: formData.currency,
        gst: formData.gst,
        moq: parseFloat(formData.moq) || 1, // NEW: Add MOQ field with default 1
        warranty: formData.warranty, // NEW: Add warranty field
        status: 'PENDING',
        colors: formData.colors.trim(),
        images: imageFiles,
        sellerName: authUser.displayName || authUser.email || ''
      };

      console.log('Adding product with status: PENDING, GST:', formData.gst, 'MOQ:', formData.moq, 'Warranty:', formData.warranty);
      
      const savedProduct = await FirebaseProductService.addProduct(productData);
      
      console.log('✅ Product added with PENDING status!', savedProduct);
      
      toast.success(`Product "${formData.name}" submitted for approval! It will be reviewed by admin.`);
      
      // Reset form
      setFormData({
        category: '',
        name: '',
        description: '',
        specifications: '',
        quantity: '',
        unit: 'PCS',
        price: '',
        currency: 'INR',
        gst: '',
        moq: '', // Reset MOQ
        warranty: '', // Reset warranty
        status: 'PENDING',
        colors: ''
      });
      
      setImages([
        {
          id: 1,
          url: null,
          name: 'Attachment 1',
          originalName: 'Attachment 1',
          isPlaceholder: true,
          isUploaded: false,
          file: null,
          previewUrl: null
        },
        {
          id: 2,
          url: null,
          name: 'Attachment 2',
          originalName: 'Attachment 2',
          isPlaceholder: true,
          isUploaded: false,
          file: null,
          previewUrl: null
        }
      ]);
      
      // Navigate back
      setTimeout(() => {
        navigate('/seller/myproducts');
      }, 1500);
      
    } catch (error) {
      console.error('❌ Failed to add product:', error);
      
      // Show user-friendly error
      if (error.message.includes('permission-denied')) {
        toast.error('Permission denied. You may not have permission to add products.');
      } else if (error.message.includes('quota')) {
        toast.error('Firestore quota exceeded. Try with smaller images or fewer images.');
      } else {
        toast.error(`Failed to add product: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    // Clean up object URLs before navigating away
    images.forEach(img => {
      if (img.previewUrl && !img.isUploaded) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
    navigate('/seller/myproducts');
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      images.forEach(img => {
        if (img.previewUrl && !img.isUploaded) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
    };
  }, [images]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-900">Loading...</p>
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">Please login to add products</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-10 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Button */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-black hover:text-gray-800 mb-4 text-sm sm:text-base"
            disabled={saving}
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Back to Products
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Add New Product</h1>
          <p className="text-gray-600 mt-1">Create a new product listing for your catalog</p>
          <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">Note:</span> All products are submitted with <span className="font-bold">PENDING</span> status and will be reviewed by admin before approval.
            </p>
          </div>
        </div>

        {/* Main Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white">
            {/* Top Row - Category and Product Name (HS Code removed) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6">
              {/* Product Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Product Category
                </label>
                <div className="relative">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleSelectChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    required
                    disabled={saving}
                  >
                    <option value="" className="text-gray-500">Select category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Product Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Product Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pr-16 sm:pr-20 text-sm sm:text-base"
                    placeholder="Enter product name"
                    maxLength="70"
                    required
                    disabled={saving}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-xs sm:text-sm text-gray-500">{charCountName} / 70</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Available Colors Field */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Available Colors
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="colors"
                  value={formData.colors}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Enter available colors (e.g., BLACK + SILVER, RED, BLUE, etc.)"
                  maxLength="100"
                  required
                  disabled={saving}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter colors separated by commas (e.g., Black, Silver, Red, Blue)
                </p>
              </div>
            </div>

            {/* Quantity, MOQ, Price, and GST Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Available Quantity
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleNumberInput}
                    className="flex-1 min-w-0 px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="e.g., 100"
                    required
                    disabled={saving}
                  />
                  <div className="relative w-28 sm:w-32">
                    <select
                      name="unit"
                      value={formData.unit}
                      onChange={handleSelectChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      disabled={saving}
                    >
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* MOQ - NEW FIELD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Minimum Order Quantity (MOQ)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="moq"
                    value={formData.moq}
                    onChange={handleNumberInput}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    placeholder="e.g., 10"
                    required
                    disabled={saving}
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <Package className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum quantity per order (must be ≤ available quantity)
                </p>
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> Price
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1 min-w-0">
                    <input
                      type="text"
                      name="price"
                      value={formData.price}
                      onChange={handleNumberInput}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 pl-8 text-sm sm:text-base"
                      placeholder="   e.g., 99.99"
                      required
                      disabled={saving}
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <IndianRupee className="h-3 w-3 text-gray-500" />
                    </div>
                  </div>
                  <div className="relative w-28 sm:w-32">
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleSelectChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      disabled={saving}
                    >
                      {currencies.map(currency => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* GST */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="text-red-500">*</span> GST Percentage
                </label>
                <div className="relative">
                  <select
                    name="gst"
                    value={formData.gst}
                    onChange={handleSelectChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                    required
                    disabled={saving}
                  >
                    <option value="" className="text-gray-500">Select GST %</option>
                    {gstRates.map(rate => (
                      <option key={rate} value={rate}>{rate}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Warranty/Guarantee Field - NEW */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Warranty / Guarantee
              </label>
              <div className="relative">
                <select
                  name="warranty"
                  value={formData.warranty}
                  onChange={handleSelectChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  required
                  disabled={saving}
                >
                  <option value="" className="text-gray-500">Select warranty period</option>
                  {warrantyOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <Shield className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Select the warranty or guarantee period for this product
              </p>
            </div>

            {/* Status Field - Hidden since it's always PENDING */}
            <div className="mb-6 hidden">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleSelectChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  disabled={saving}
                >
                  <option value="PENDING">PENDING</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Note: All new products are submitted for admin approval
              </p>
            </div>

            {/* Product Description */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Product Description
              </label>
              <div className="relative">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm sm:text-base"
                  placeholder="Enter product description..."
                  maxLength="255"
                  required
                  disabled={saving}
                />
                <div className="absolute bottom-3 right-3 text-xs sm:text-sm text-gray-500">
                  {charCountDesc} / 255
                </div>
              </div>
            </div>

            {/* Product Specifications */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <span className="text-red-500">*</span> Product Specifications
              </label>
              <div className="relative">
                <textarea
                  name="specifications"
                  value={formData.specifications}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm sm:text-base"
                  placeholder="Enter product specifications (one per line)..."
                  maxLength="500"
                  required
                  disabled={saving}
                />
                <div className="absolute bottom-3 right-3 text-xs sm:text-sm text-gray-500">
                  {charCountSpec} / 500
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter each specification on a new line
              </p>
            </div>

            {/* Product Images Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Product Images</h2>
                {images.length < 6 && (
                  <button
                    type="button"
                    onClick={addImageSlot}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-800 text-white text-sm font-medium rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                    disabled={saving}
                  >
                    <Plus className="w-4 h-4" />
                    Add Image Slot
                  </button>
                )}
              </div>
              
              <div className="flex flex-col lg:flex-row gap-6 sm:gap-8">
                {/* Left side - Image placeholders */}
                <div className="flex flex-wrap justify-center sm:justify-start gap-4 sm:gap-6">
                  {images.map((image) => (
                    <div key={image.id} className="w-full sm:w-auto">
                      <div className="relative">
                        <div 
                          className={`w-full sm:w-48 h-48 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all ${
                            image.url || image.previewUrl 
                              ? 'border-2 border-gray-300 bg-gray-50 overflow-hidden' 
                              : 'border-2 border-dashed border-gray-300 bg-gray-50 hover:border-blue-400'
                          }`}
                          onClick={() => {
                            if (!saving) {
                              if (image.url || image.previewUrl) {
                                viewImage(image);
                              } else {
                                handleAttachmentBoxClick(image.id);
                              }
                            }
                          }}
                        >
                          {image.url || image.previewUrl ? (
                            <>
                              <img
                                src={image.url || image.previewUrl}
                                alt={image.name}
                                className="w-full h-full object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/400x400?text=Image+Error';
                                }}
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all rounded-lg" />
                            </>
                          ) : (
                            <>
                              <div className="flex flex-col items-center p-4">
                                <Upload className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-2" />
                                <span className="text-gray-500 text-sm text-center">{image.name}</span>
                                <span className="text-gray-400 text-xs mt-1 text-center">Click to upload</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Main image indicator */}
                        {image.id === 1 && (image.url || image.previewUrl) && (
                          <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                            Main Image
                          </div>
                        )}
                      </div>
                      
                      {/* Error message */}
                      {imageErrors[image.id] && (
                        <div className="mt-2 text-xs text-red-600 text-center sm:text-left">
                          {imageErrors[image.id]}
                        </div>
                      )}
                      
                      {/* Action buttons below */}
                      <div className="flex gap-2 mt-3 justify-center flex-wrap">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!saving) {
                              document.getElementById(`file-${image.id}`).click();
                            }
                          }}
                          className={`p-2 transition-colors rounded-md disabled:opacity-50 ${
                            image.url || image.previewUrl
                              ? 'text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100'
                              : 'text-gray-600 hover:text-blue-600 bg-gray-100 hover:bg-gray-200'
                          }`}
                          title={image.url || image.previewUrl ? "Replace Image" : "Upload Image"}
                          disabled={saving}
                        >
                          {image.url || image.previewUrl ? (
                            <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                          ) : (
                            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                          )}
                        </button>
                        
                        {(image.url || image.previewUrl) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!saving) {
                                viewImage(image);
                              }
                            }}
                            className="p-2 text-gray-600 hover:text-green-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                            title="View Image"
                            disabled={saving}
                          >
                            <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        )}
                        
                        {(image.url || image.previewUrl) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!saving && window.confirm('Are you sure you want to remove this image?')) {
                                removeImage(image.id);
                              }
                            }}
                            className="p-2 text-gray-600 hover:text-red-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                            title="Remove Image"
                            disabled={saving}
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        )}
                        
                        <input
                          id={`file-${image.id}`}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={(e) => handleImageUpload(image.id, e)}
                          className="hidden"
                          disabled={saving}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right side - Tips */}
                <div className="flex-1">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Info className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-blue-900 mb-3">Tips</h3>
                        <ul className="text-sm text-gray-700 space-y-2">
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span>Supported file types: .jpg, .jpeg, .png, .webp</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span>File must not be more than 5MB</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span>Make sure you have good resolution</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span>Photo resolution must be more than 600×600 resolution</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span>First image will be displayed as main product image</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span className="text-red-500 font-medium">Main image (first image) is required</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-400 mr-2">•</span>
                            <span className="text-blue-600 font-medium">MOQ must be less than or equal to available quantity</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleBack}
                className="px-6 sm:px-8 py-2.5 border border-gray-300 bg-white text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 order-2 sm:order-1"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 sm:px-8 py-2.5 bg-orange-600 hover:bg-orange-800 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px] order-1 sm:order-2"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : 'Submit for Approval'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex justify-between items-center">
              <div className="max-w-[70%]">
                <h3 className="text-lg font-semibold text-gray-800 truncate">
                  {selectedImage.name}
                </h3>
                <p className="text-sm text-gray-500 mt-1 truncate">
                  {selectedImage.id === 1 ? 'Main Product Image' : 'Additional Image'}
                </p>
              </div>
              <button
                onClick={closeImageViewer}
                className="text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 ml-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <div className="flex justify-center">
                <img
                  src={selectedImage.url || selectedImage.previewUrl}
                  alt={selectedImage.name}
                  className="max-w-full max-h-[60vh] sm:max-h-[70vh] mx-auto rounded-lg"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                  }}
                />
              </div>
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    closeImageViewer();
                    removeImage(selectedImage.id);
                  }}
                  className="px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-md text-sm font-medium"
                >
                  Remove This Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProductPage;